import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "react-bootstrap";
import MessagingList from "./messagingList";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import { useDispatch, useSelector } from "react-redux";

import {
  addAppAdhocNewMessage,
  addAppMessage,
  getAddedNewMessageStatus,
  getAppAdhocMessages,
  getAppMsgList,
  getAppMsgReadStatus,
  loadAppAdhocMessages,
  loadAppMsgList,
  sendMessageThroughEmail,
  updateMessageRead,
  updateMsgListRead
} from "../../app/store/messages";

import {
  getCurrentUser,
  getUserProfilePic,
  getUserProfilePicTwo,
  loadUserProfilePic,
  loadUserProfilePicTwo
} from "../../app/store/auth";

import { getAlert } from "../../services/appAlerts";

const toolbar = [
  [
    "undo",
    "redo",
    "font",
    "fontSize",
    "formatBlock",
    "bold",
    "underline",
    "italic",
    "fontColor",
    "hiliteColor",
    "removeFormat",
    "link",
    "image"
  ]
];

const MessagingPopup = ({ workflowTask, messageType, message, handleReadStatus }) => {
  const dispatch = useDispatch();
  const currentUser = getCurrentUser();

  // State
  const [messageList, setMessageList] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [content, setContent] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);

  // Selectors
  const msgListSelector = useSelector(getAppMsgList);
  const msgReadStatusSelector = useSelector(getAppMsgReadStatus);
  const adhocMsgsSelector = useSelector(getAppAdhocMessages);
  const addedStatusSelector = useSelector(getAddedNewMessageStatus);
  const userProfilePic = useSelector(getUserProfilePic);
  const userProfilePicTwo = useSelector(getUserProfilePicTwo);

  // Profile pictures
  const [profilePicData, setProfilePicData] = useState(null);
  const [profilePicTwoData, setProfilePicTwoData] = useState(null);

  // -----------------------------
  // Load current user's picture
  // -----------------------------
  useEffect(() => {
    if (currentUser?.id) {
      dispatch(loadUserProfilePic(currentUser.id));
    }
  }, []);

  useEffect(() => {
    if (userProfilePic) {
      setProfilePicData(userProfilePic);
    }
  }, [userProfilePic]);

  useEffect(() => {
    if (userProfilePicTwo) {
      setProfilePicTwoData(userProfilePicTwo);
    }
  }, [userProfilePicTwo]);

  // -----------------------------
  // Load messages based on type
  // -----------------------------
  useEffect(() => {
    setDocuments([]);
    setContent("");
    setMessageList([]);

    if (messageType === "Adhoc" && workflowTask?.wiid > 0) {
      dispatch(loadAppAdhocMessages(workflowTask.wiid));
    } else if (message?.id > 0) {
      dispatch(loadAppMsgList(message.id));
    }
  }, [message, workflowTask]);

  // -----------------------------
  // Handle normal message list
  // -----------------------------
  useEffect(() => {
    if (msgListSelector.length === 0) return;

    const updatedList = msgListSelector.map(item => {
      const isSender = item.fromId === parseInt(currentUser.id);
      const performer = `${item.fromName} ${item.fromLastName}`;
      if (!isSender) dispatch(loadUserProfilePicTwo(item.fromId));



      return {
        author: isSender ? "send" : "receive",
        data: item.message,
        performer,
        date: item.messageDate,
        documents: item.documents,
        id: item.id,
        readStatus: item.toReadStatus
      };
    });

    setMessageList(updatedList);

    const unread = updatedList.filter(x => !x.readStatus);
    if (unread.length > 0 && message?.id) {
      dispatch(updateMessageRead(message.id, currentUser.id));
    }
  }, [msgListSelector]);

  // Read status update to parent
  useEffect(() => {
    if (!msgReadStatusSelector) return;

    const unreadIds = messageList
      .filter(m => !m.readStatus)
      .map(m => m.id)
      .join(",");

    if (unreadIds) {
      handleReadStatus(unreadIds);
      updateMsgListRead(false);
    }
  }, [msgReadStatusSelector]);

  // -----------------------------
  // Handle Adhoc Messages
  // -----------------------------
  useEffect(() => {
    if (adhocMsgsSelector.length === 0) return;

    const converted = adhocMsgsSelector.map(item => ({
      author: item.fromId === workflowTask.performerId ? "send" : "receive",
      data: item.message,
      performer: item.fromName + " " + item.fromLastName,
      date: item.messageDate,
      documents: item.documents,
      id: item.id,
      readStatus: item.readStatus
    }));

    setMessageList(converted);
  }, [adhocMsgsSelector]);

  // -----------------------------
  // Editor and file upload
  // -----------------------------
  const handleChange = useCallback(content => setContent(content), []);

  const onFileChange = useCallback(event => {
    const newDocs = Array.from(event.target.files).map(file => ({
      name: file.name,
      type: file.type,
      size: (file.size / 1024).toFixed(2),
      file
    }));
    setDocuments(prev => [...prev, ...newDocs]);
  }, []);

  const handleFileRemove = file =>
    setDocuments(prev => prev.filter(f => f !== file));

  // -----------------------------
  // Drag & Drop
  // -----------------------------
  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);

    const dropped = Array.from(e.dataTransfer.files).map(file => ({
      name: file.name,
      type: file.type,
      size: (file.size / 1024).toFixed(2),
      file
    }));

    setDocuments(prev => [...prev, ...dropped]);
  };

  // -----------------------------
  // Submit Message
  // -----------------------------
  const onSubmit = () => {
    if (!content.trim()) {
      getAlert("", "Enter Message", "error");
      return;
    }

    const data = new FormData();
    data.append("message", content);
    documents.forEach((doc, idx) => data.append(`files[${idx}]`, doc.file));

    if (messageType === "Adhoc") {
      data.append("workflowTask", JSON.stringify(workflowTask));
      dispatch(addAppAdhocNewMessage(data));
    } else {
      data.append("performerId", currentUser.id);
      data.append("communication", JSON.stringify(message));
      const emailData = new FormData(data);

      emailData.append("Msg", content);
      emailData.append("MsgType", "");
      emailData.append("task", JSON.stringify(workflowTask));

      dispatch(addAppMessage(data));
      dispatch(sendMessageThroughEmail(emailData));
    }

    setContent("");
    setDocuments([]);
  };

  // Refresh list after new message sent
  useEffect(() => {
    if (!addedStatusSelector?.state) return;

    if (message?.id > 0) {
      dispatch(loadAppMsgList(message.id));
    }

    setContent("");
    setDocuments([]);
  }, [addedStatusSelector]);

  // -----------------------------
  // Component Return
  // -----------------------------
  return (
    <>
      {/* MESSAGE LIST */}
      <div
        className="row"
        style={{
          maxHeight: 350,
          overflow: "auto",
          display: "flex",
          flexDirection: "column-reverse"
        }}
      >
        <div className="col-md-12 bg-white" style={{ minHeight: "300px" }}>
          {messageList.map(item => (
            <MessagingList
              key={item.id}
              message={item}
              profilePicData={profilePicData}
              profilePicTwoData={profilePicTwoData}
            />
          ))}
        </div>
      </div>

      {/* DROPZONE */}
      <div
        className={`drop-zone w-100 p-4 mb-3 text-center border rounded ${isDragging ? "bg-light" : ""
          }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current.click()}
        style={{
          border: "2px dotted #ccc",
          background: isDragging ? "#f0f8ff" : "#fafafa",
          cursor: "pointer"
        }}
      >
        <i className="fa fa-cloud-upload fa-2x mb-2" />
        <p>{isDragging ? "Drop files here..." : "Drag & drop or click to upload"}</p>
      </div>

      {/* FILE PREVIEW */}
      {documents.length > 0 && (
        <div className="file-preview">
          {documents.map((file, idx) => (
            <div key={idx} className="file-preview-item ml-3">
              <span>{file.name}</span>
              <span className="badge badge-light ml-2">{file.size} KB</span>
              <button className="btn btn-link btn-sm" onClick={() => handleFileRemove(file)}>
                <i className="fa fa-times text-danger" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* EDITOR */}
      <div className="col-md-12 mt-3">
        <SunEditor
          setOptions={{ height: 50, buttonList: toolbar }}
          onChange={handleChange}
          setContents={content}
        />
      </div>

      {/* SEND BUTTON */}
      <div className="col-12 text-center mt-3">
        <Button variant="outline-dark" size="sm" onClick={onSubmit}>
          <i className="fa fa-paper-plane-o mr-2" />
          Send
        </Button>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileChange}
        multiple
        hidden
      />
    </>
  );
};

export default MessagingPopup;
